import React, { useState, useEffect, createContext, useContext } from 'react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Cookie, Settings, Shield, BarChart3, Target, X } from 'lucide-react';

// Cookie Context
const CookieContext = createContext();

export const useCookies = () => {
  const context = useContext(CookieContext);
  if (!context) {
    throw new Error('useCookies must be used within a CookieProvider');
  }
  return context;
};

const defaultPreferences = {
  essential: true, // Always true, can't be disabled
  analytics: false,
  marketing: false,
  preferences: false,
};

export const CookieProvider = ({ children }) => {
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [hasConsented, setHasConsented] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Load preferences from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('vetordv_cookie_consent');
    if (saved) {
      const parsed = JSON.parse(saved);
      setPreferences(parsed.preferences);
      setHasConsented(true);
      setShowBanner(false);
    } else {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const savePreferences = (newPreferences) => {
    const data = {
      preferences: { ...newPreferences, essential: true },
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    localStorage.setItem('vetordv_cookie_consent', JSON.stringify(data));
    setPreferences(data.preferences);
    setHasConsented(true);
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptAll = () => {
    savePreferences({
      essential: true,
      analytics: true,
      marketing: true,
      preferences: true,
    });
  };

  const rejectAll = () => {
    savePreferences({
      essential: true,
      analytics: false,
      marketing: false,
      preferences: false,
    });
  };

  const openSettings = () => {
    setShowSettings(true);
  };

  const resetConsent = () => {
    localStorage.removeItem('vetordv_cookie_consent');
    setPreferences(defaultPreferences);
    setHasConsented(false);
    setShowBanner(true);
  };

  return (
    <CookieContext.Provider value={{ 
      preferences, 
      hasConsented, 
      acceptAll, 
      rejectAll, 
      savePreferences,
      openSettings,
      resetConsent 
    }}>
      {children}
      
      {/* Cookie Banner */}
      {showBanner && !hasConsented && (
        <CookieBanner 
          onAcceptAll={acceptAll}
          onRejectAll={rejectAll}
          onCustomize={() => setShowSettings(true)}
        />
      )}

      {/* Cookie Settings Modal */}
      <CookieSettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        preferences={preferences}
        onSave={savePreferences}
      />
    </CookieContext.Provider>
  );
};

const CookieBanner = ({ onAcceptAll, onRejectAll, onCustomize }) => {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slideInUp">
      <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        <div className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6">
            {/* Icon & Text */}
            <div className="flex items-start gap-4 flex-1">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Cookie className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-1">
                  Nous respectons votre vie privée
                </h3>
                <p className="text-sm text-gray-600">
                  Nous utilisons des cookies pour améliorer votre expérience, analyser le trafic et personnaliser le contenu. 
                  Vous pouvez accepter tous les cookies, les refuser ou personnaliser vos préférences.
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
              <Button
                variant="outline"
                onClick={onCustomize}
                className="text-gray-600"
              >
                <Settings className="w-4 h-4 mr-2" />
                Personnaliser
              </Button>
              <Button
                variant="outline"
                onClick={onRejectAll}
                className="text-gray-600"
              >
                Tout refuser
              </Button>
              <Button
                onClick={onAcceptAll}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                Tout accepter
              </Button>
            </div>
          </div>
        </div>

        {/* Footer links */}
        <div className="bg-gray-50 px-6 py-3 flex items-center justify-between text-xs text-gray-500">
          <span>En savoir plus sur notre <a href="/confidentialite" className="text-green-600 hover:underline">politique de confidentialité</a></span>
        </div>
      </div>
    </div>
  );
};

const CookieSettingsModal = ({ isOpen, onClose, preferences, onSave }) => {
  const [localPrefs, setLocalPrefs] = useState(preferences);

  useEffect(() => {
    setLocalPrefs(preferences);
  }, [preferences]);

  const cookieTypes = [
    {
      id: 'essential',
      name: 'Cookies essentiels',
      description: 'Nécessaires au fonctionnement du site. Ils permettent la navigation et l\'accès aux zones sécurisées.',
      icon: Shield,
      required: true
    },
    {
      id: 'analytics',
      name: 'Cookies analytiques',
      description: 'Nous aident à comprendre comment les visiteurs utilisent le site pour l\'améliorer.',
      icon: BarChart3,
      required: false
    },
    {
      id: 'marketing',
      name: 'Cookies marketing',
      description: 'Utilisés pour afficher des publicités pertinentes et mesurer leur efficacité.',
      icon: Target,
      required: false
    },
    {
      id: 'preferences',
      name: 'Cookies de préférences',
      description: 'Permettent de mémoriser vos choix (langue, région) pour personnaliser votre expérience.',
      icon: Settings,
      required: false
    }
  ];

  const handleToggle = (id) => {
    if (id === 'essential') return; // Can't toggle essential
    setLocalPrefs(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSave = () => {
    onSave(localPrefs);
  };

  const handleAcceptAll = () => {
    onSave({
      essential: true,
      analytics: true,
      marketing: true,
      preferences: true,
    });
  };

  const handleRejectAll = () => {
    onSave({
      essential: true,
      analytics: false,
      marketing: false,
      preferences: false,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cookie className="w-5 h-5 text-green-600" />
            Paramètres des cookies
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm text-gray-600 mb-6">
            Gérez vos préférences en matière de cookies. Les cookies essentiels sont nécessaires 
            au fonctionnement du site et ne peuvent pas être désactivés.
          </p>

          {/* Cookie categories */}
          <div className="space-y-4">
            {cookieTypes.map((cookie) => (
              <div 
                key={cookie.id}
                className={`p-4 rounded-xl border ${
                  localPrefs[cookie.id] ? 'border-green-200 bg-green-50/50' : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      localPrefs[cookie.id] ? 'bg-green-100' : 'bg-gray-200'
                    }`}>
                      <cookie.icon className={`w-5 h-5 ${
                        localPrefs[cookie.id] ? 'text-green-600' : 'text-gray-500'
                      }`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-800">{cookie.name}</h4>
                        {cookie.required && (
                          <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                            Requis
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{cookie.description}</p>
                    </div>
                  </div>
                  <Switch
                    checked={localPrefs[cookie.id]}
                    onCheckedChange={() => handleToggle(cookie.id)}
                    disabled={cookie.required}
                    className="data-[state=checked]:bg-green-500"
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 mt-6 pt-6 border-t">
            <Button
              variant="outline"
              onClick={handleRejectAll}
              className="flex-1"
            >
              Tout refuser
            </Button>
            <Button
              variant="outline"
              onClick={handleAcceptAll}
              className="flex-1"
            >
              Tout accepter
            </Button>
            <Button
              onClick={handleSave}
              className="flex-1 bg-green-500 hover:bg-green-600 text-white"
            >
              Enregistrer mes choix
            </Button>
          </div>

          {/* Link to privacy policy */}
          <p className="text-xs text-gray-400 text-center mt-4">
            Pour plus d'informations, consultez notre{' '}
            <a href="/confidentialite" className="text-green-600 hover:underline">
              politique de confidentialité
            </a>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Small cookie button to reopen settings
export const CookieSettingsButton = () => {
  const { openSettings, hasConsented } = useCookies();

  if (!hasConsented) return null;

  return (
    <button
      onClick={openSettings}
      className="fixed bottom-4 left-4 z-40 w-12 h-12 bg-white rounded-full shadow-lg border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-colors group"
      title="Paramètres des cookies"
    >
      <Cookie className="w-5 h-5 text-gray-600 group-hover:text-green-600 transition-colors" />
    </button>
  );
};

// CSS Animation for banner
const style = document.createElement('style');
style.textContent = `
  @keyframes slideInUp {
    from {
      opacity: 0;
      transform: translateY(100%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  .animate-slideInUp {
    animation: slideInUp 0.5s ease-out;
  }
`;
document.head.appendChild(style);

export default CookieProvider;
